import axios from "axios";
import { BACKEND_BASE } from "./backendBase";

const BACKEND_URL = BACKEND_BASE;
export const API = `${BACKEND_URL}/api`;

// Bearer-token fallback for clients where third-party / SameSite=None cookies
// are unreliable (iOS Safari ITP, PWA standalone-mode cookie isolation,
// in-app browser webviews). The token is captured on every auth-grant
// response (login / register / invite-accept / admin-reset) and attached as
// an Authorization header on every subsequent request. Cookies still work
// in parallel — this is purely additive, defense-in-depth auth.
const TOKEN_KEY = "tt_auth_token";

export function getAuthToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* localStorage disabled (rare iOS private-mode quirk) — silently degrade */
  }
}

export function clearAuthToken() {
  setAuthToken(null);
}

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// ---------------------------------------------------------------------------
// Transient-error retry with exponential backoff.
// ---------------------------------------------------------------------------
// The app is fronted by Cloudflare → Kubernetes ingress → FastAPI. Any of:
//   - Cloudflare 520-524 (origin unreachable / timeout / unknown)
//   - K8s pod cold-start / rolling-update 502/503
//   - Network blip with `err.response == null`
// can flash a scary red toast at the customer for what's actually a 1-2s
// transient. We retry these silently for *idempotent* requests only —
// never POST/PUT/PATCH/DELETE (would risk double-create / double-charge).
//
// Backoff: ~500ms, then ~1500ms. 2 retries max → 3 attempts total.
// Total worst-case wall time: ~2.5s + per-attempt timeout.
//
// 401 is excluded (we already clear the token and let auth context handle).
// 408/429 are intentionally retried — they're explicit "try again" signals.
const RETRIABLE_METHODS = new Set(["get", "head", "options"]);
const RETRIABLE_STATUSES = new Set([
  408, // Request Timeout
  429, // Too Many Requests
  500, // Internal Server Error (rare on FastAPI but possible)
  502, // Bad Gateway (K8s ingress mid-rollout)
  503, // Service Unavailable (pod cold start)
  504, // Gateway Timeout
  520, // Cloudflare: Web Server Returned an Unknown Error
  521, // Cloudflare: Web Server Is Down
  522, // Cloudflare: Connection Timed Out
  523, // Cloudflare: Origin Is Unreachable
  524, // Cloudflare: A Timeout Occurred
]);
const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [500, 1500, 4000];

function _shouldRetry(error) {
  const cfg = error?.config;
  if (!cfg) return false;
  // Don't retry requests that explicitly opt out (e.g., a one-shot
  // health-check ping that doesn't want backoff added to its budget).
  if (cfg.retry === false) return false;
  const method = String(cfg.method || "get").toLowerCase();
  if (!RETRIABLE_METHODS.has(method)) return false;
  // Network error (no response from server at all) → almost always
  // worth one retry on idempotent calls.
  if (!error.response) return true;
  return RETRIABLE_STATUSES.has(error.response.status);
}

function _sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    // 401: drop the local token so a stale one stops being sent. The
    // auth context's `/auth/me` poll will route the user back to /login.
    // Never retry 401 — the next attempt would 401 again instantly.
    if (err.response && err.response.status === 401) {
      clearAuthToken();
      return Promise.reject(err);
    }

    if (_shouldRetry(err)) {
      const cfg = err.config;
      cfg.__retryCount = cfg.__retryCount || 0;
      if (cfg.__retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAYS_MS[cfg.__retryCount] ?? 1500;
        cfg.__retryCount += 1;
        // Lightweight observability: warn (not error) so it doesn't trip
        // error-tracking integrations on every transient. Includes the
        // attempt number + status so we can see retry pressure in
        // session-replay tools.
        try {
          console.warn(
            `[api] retry ${cfg.__retryCount}/${MAX_RETRIES} after ${delay}ms`,
            {
              url: cfg.url,
              method: cfg.method,
              status: err.response?.status ?? "network",
            },
          );
        } catch {
          /* noop */
        }
        await _sleep(delay);
        return api(cfg);
      }
    }

    return Promise.reject(err);
  },
);

export default api;

// Infrastructure noise (Cloudflare 52x pages, gateway errors, raw HTML)
// must NEVER reach a customer's screen verbatim — real incident Mar 2026:
// the map showed "The origin web server sent a response that Cloudflare
// could not parse" during a production origin blip.
const INFRA_ERROR_RE =
  /cloudflare|origin web server|bad gateway|gateway time-?out|upstream|<html|<!doctype|error code: 5\d\d/i;
const FRIENDLY_INFRA_MSG =
  "TruckTrackr is having a brief hiccup — hang tight, we're retrying.";

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") {
    if (INFRA_ERROR_RE.test(detail)) return FRIENDLY_INFRA_MSG;
    return detail;
  }
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  const s = String(detail);
  return INFRA_ERROR_RE.test(s) ? FRIENDLY_INFRA_MSG : s;
}
